// §5 — Third-Party Fees. Ported from FeeTable.jsx (single-line inline rows +
// DispositionCell + multi-payer splits). Data plumbing is remapped to the
// production contract: fees arrive as a PROP (DocOrderFee[], camelCase) and every
// edit autosaves through ctx.saveFee / ctx.addManualFee — no local fee array.
// All completeness / discrepancy / split / status rules come from doc-order-core
// (zero reimplementation). Upload affordances (upload button, InvoiceUploadPanel)
// stay omitted until P4 uploads; the FeePeek eye popover ships (metadata from the
// eFolder mirror — Jennifer bug 7). Manual-fee delete omitted (the contract
// exposes no removeFee). Split model per Jennifer's testing revision (bugs
// 10/11): the parent row is the TOTAL only; all portions render below and must
// sum to it.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Btn, Icon, IconBtn, StatusPill } from '../atoms';
import { useDocOrderCtx } from '../context';
import { fmtDate, fmtMoney, fmtStamp } from '../format';
import {
  activeFiledInvoices,
  costToCureCheck,
  feeInvoiceStatus,
  feeReconciliationWorkflowOpen,
  feeNeedsDisposition,
  invoiceHasDiscrepancy,
  invoiceTotal,
  isCurrentFeeAck,
  feeInvoiceWorkflowOpen,
  isCurrentFeeEscalation,
  splitsValid,
  DO_TOLERANCE,
  type DocOrderFee,
  type FeeSplit,
  type PaidBy
} from '../core';
import { ReconcileSheet } from './ReconcileSheet';
import { EncompassDiffSheet } from './EncompassDiffSheet';
import { CostToCureSheet } from './CostToCureSheet';
import { EscalationSheet } from './EscalationSheet';
import { UploadPanel } from './UploadPanel';
import { getAttachmentUrl } from '@/services/encompassDocumentService';

// ---- FeePeek — the invoice eye popover (port of the prototype FeePeek,
// Jennifer bug 7). Lists the fee's mapped eFolder folder + its active files
// (name/date) from the webhook-maintained mirror. Files whose bytes were
// downloaded (storagePath present) are clickable → signed URL → new tab (#7);
// metadata-only mirror rows render disabled with an explanatory tooltip.
// Rendered INLINE with fixed positioning — NO body portal — so it stays
// interactive inside the Radix LoanDetailsSheet's modal pointer-events lock.
interface FeePeekDocs {
  folder: string;
  docs: Array<{ file: string; at: string | null; storagePath?: string | null }>;
}

function FeePeek({ id, data, addToast }: { id: string; data: FeePeekDocs; addToast: (m: string, i?: string) => void }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  // #7: open a downloaded eFolder file in a new tab. The blank tab is opened
  // SYNCHRONOUSLY in the click gesture (popup-blocker-safe) and only navigated
  // once the signed URL resolves (the EncompassEfolderTab.handleView pattern).
  const viewDoc = useCallback(
    async (storagePath: string) => {
      const tab = window.open('', '_blank');
      if (!tab) {
        addToast('Popup blocked — allow popups to view the document', 'alertTriangle');
        return;
      }
      try {
        const url = await getAttachmentUrl(storagePath);
        if (url) {
          tab.opener = null;
          tab.location.href = url;
        } else {
          tab.close();
          addToast('Could not open the document', 'alertTriangle');
        }
      } catch {
        tab.close();
        addToast('Could not open the document', 'alertTriangle');
      }
    },
    [addToast]
  );

  const updatePos = useCallback(() => {
    if (!btnRef.current) {
return;
}
    const r = btnRef.current.getBoundingClientRect();
    const popW = 300;
    // Anchor below+right of the button; nudge left if it would overflow.
    let left = r.right - popW;
    if (left < 8) {
left = 8;
}
    setPos({ top: r.bottom + 6, left });
  }, []);

  useEffect(() => {
    if (!open) {
return;
}
    updatePos();
    const onClose = (e: MouseEvent) => {
      if (btnRef.current?.contains(e.target as Node)) {
return;
}
      if (popRef.current?.contains(e.target as Node)) {
return;
}
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    };
    window.addEventListener('scroll', updatePos, true);
    window.addEventListener('resize', updatePos);
    document.addEventListener('mousedown', onClose);
    document.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('scroll', updatePos, true);
      window.removeEventListener('resize', updatePos);
      document.removeEventListener('mousedown', onClose);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open, updatePos]);

  return (
    <div className="fee-peek-wrap">
      <button
        ref={btnRef}
        type="button"
        className={`fee-peek-btn ${open ? 'is-open' : ''}`.trim()}
        title={`View current documents in ${data.folder}`}
        aria-label={`View current documents in ${data.folder}`}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="eye" size={13} />
      </button>
      {open && (
        <div
          ref={popRef}
          id={`fee-peek-${id}`}
          className="fee-peek-pop fee-peek-pop-portal"
          style={{ top: pos.top, left: pos.left }}
        >
          <div className="fpp-head">
            <Icon name="folder" size={11} />
            <span className="fpp-folder">{data.folder}</span>
            <button type="button" className="fpp-close" onClick={() => setOpen(false)} aria-label="Close">
              <Icon name="x" size={11} />
            </button>
          </div>
          {data.docs.length === 0 ? (
            <div className="fpp-empty">No current documents in this folder.</div>
          ) : (
            <ul className="fpp-list">
              {data.docs.map((d, di) => {
                const openable = !!d.storagePath;
                return (
                  <li key={`${d.file}|${di}`} className="fpp-item">
                    {openable ? (
                      <button
                        type="button"
                        className="fpp-open"
                        title={`Open ${d.file} in a new tab`}
                        onClick={() => viewDoc(d.storagePath as string)}
                      >
                        <Icon name="fileText" size={12} />
                        <span className="fpp-name">{d.file}</span>
                        {d.at && <span className="fpp-meta">{fmtDate(d.at)}</span>}
                        <Icon name="externalLink" size={11} />
                      </button>
                    ) : (
                      <span className="fpp-static" title="Not downloaded — open it from the Encompass eFolder">
                        <Icon name="fileText" size={12} />
                        <span className="fpp-name">{d.file}</span>
                        {d.at && <span className="fpp-meta">{fmtDate(d.at)}</span>}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ---- DispositionCell — the "Paid Upfront / By / Reimb" control, shared by the
// main fee row and by each split portion so they render identically. Pure UI
// state machine over a { paidBy, timing, reimburse } disposition; emits a patch.
type Disposition = Pick<DocOrderFee, 'paidBy' | 'timing' | 'reimburse'>;
type DispositionPatch = Partial<Pick<DocOrderFee, 'paidBy' | 'timing' | 'reimburse'>>;

/** Read-only payer label (presentation only — not a core rule). */
const PAID_BY_LABEL: Record<PaidBy, string> = { Borrower: 'Borrower', LO: 'LO', Lendwise: 'Lendwise' };

interface DispositionCellProps {
  disp: Disposition;
  editable: boolean;
  onChange: (patch: DispositionPatch) => void;
  charged?: boolean;
}

function DispositionCell({ disp, editable, onChange, charged = true }: DispositionCellProps) {
  const paidUpfront =
    disp.paidBy === 'LO' || disp.paidBy === 'Lendwise' || (disp.paidBy === 'Borrower' && disp.timing === 'POC');
  const upfrontBy: PaidBy | null = paidUpfront ? disp.paidBy : null;
  const isReimbursed = disp.reimburse === true;
  const borrowerPoc = disp.paidBy === 'Borrower' && disp.timing === 'POC';
  // "No" (collected at closing) is an ACTIVE choice only when PAC Borrower is set;
  // until then the control is unset and either click must register (no no-op).
  const isPacBorrower = disp.paidBy === 'Borrower' && disp.timing === 'PAC';
  const dispUnset = !paidUpfront && !isPacBorrower;

  const setNotUpfront = () => onChange({ paidBy: 'Borrower', timing: 'PAC', reimburse: false });
  const setUpfrontDefault = () => onChange({ paidBy: 'LO', timing: 'PAC', reimburse: true });
  const setUpfrontBy = (who: PaidBy) => {
    if (who === 'Borrower') {
onChange({ paidBy: 'Borrower', timing: 'POC', reimburse: false });
} else {
onChange({ paidBy: who, timing: 'PAC', reimburse: true });
}
  };
  const setReimburse = (yes: boolean) => onChange({ timing: yes ? 'PAC' : 'POC', reimburse: yes });

  if (!charged) {
    return (
      <span className="followup-static" style={{ color: 'var(--fg-subtle)' }}>
        —
      </span>
    );
  }

  return (
    <div className="disp-cell">
      <span className="disp-step">
        <span className="disp-step-lbl disp-step-lbl-stacked">
          <span>Paid</span>
          <span>Upfront</span>
        </span>
        {editable ? (
          <div className={`disp-seg disp-seg-yn ${dispUnset ? 'disp-seg-unset' : ''}`}>
            <button className={paidUpfront ? 'active yes' : 'yes'} onClick={() => !paidUpfront && setUpfrontDefault()}>
              Yes
            </button>
            <button className={isPacBorrower ? 'active no' : 'no'} onClick={() => !isPacBorrower && setNotUpfront()}>
              No
            </button>
          </div>
        ) : dispUnset ? (
          <span className="disp-pill disp-pill-poc">—</span>
        ) : (
          <span className={`disp-pill ${paidUpfront ? 'disp-pill-default' : 'disp-pill-poc'}`}>
            {paidUpfront ? 'Yes' : 'No'}
          </span>
        )}
      </span>

      {/* POC override / paid-at-closing pill sits after the Paid-Upfront step. */}
      {borrowerPoc && <span className="disp-pill disp-pill-info">POC · Borrower</span>}
      {isPacBorrower && <span className="disp-pill disp-pill-info">Paid at Closing</span>}

      {paidUpfront && !borrowerPoc && (
        <span className="disp-step disp-step-by">
          <span className="disp-step-lbl">By</span>
          {editable ? (
            <div className="disp-seg disp-seg-by">
              <button className={upfrontBy === 'Borrower' ? 'active' : ''} onClick={() => setUpfrontBy('Borrower')}>
                Borr
              </button>
              <button className={upfrontBy === 'LO' ? 'active' : ''} onClick={() => setUpfrontBy('LO')}>
                LO
              </button>
              <button className={upfrontBy === 'Lendwise' ? 'active' : ''} onClick={() => setUpfrontBy('Lendwise')}>
                Lendwise
              </button>
            </div>
          ) : (
            <span className={`paidby-pill paidby-${upfrontBy}`}>{upfrontBy ? PAID_BY_LABEL[upfrontBy] : ''}</span>
          )}
        </span>
      )}

      {paidUpfront && (upfrontBy === 'LO' || upfrontBy === 'Lendwise') && (
        <span className="disp-step">
          <span className="disp-step-lbl">Reimb</span>
          {editable ? (
            <div className="disp-seg disp-seg-yn">
              <button className={isReimbursed ? 'active yes' : 'yes'} onClick={() => setReimburse(true)}>
                Yes
              </button>
              <button className={!isReimbursed ? 'active no' : 'no'} onClick={() => setReimburse(false)}>
                No
              </button>
            </div>
          ) : isReimbursed ? (
            <StatusPill kind="gold" icon="rotateCcw">
              Yes
            </StatusPill>
          ) : (
            <StatusPill kind="neutral" icon="check">
              No
            </StatusPill>
          )}
        </span>
      )}
    </div>
  );
}

interface FeeTableProps {
  fees: DocOrderFee[];
  stepNo: number;
}

const mkSplitId = () => `sp-${  Math.random().toString(36).slice(2, 7)}`;

export function FeeTable({ fees, stepNo }: FeeTableProps) {
  const { editable, role, stage, invoiceFolderStatus, invoiceFolderDocs, saveFee, addManualFee, closingEditFee, approveFee, retryWrites, deleteFee, addToast, writebacks, busy } = useDocOrderCtx();
  const isClosing = role === 'closing';

  // Which fee's ReconcileSheet is open (FeeTable owns this open state).
  const [reconcileId, setReconcileId] = useState<string | null>(null);
  // Which fee's inline invoice UploadPanel is expanded (one at a time).
  const [uploadingFeeId, setUploadingFeeId] = useState<string | null>(null);
  // Post-draft: the server stamps invoiceReject.afterSubmit, but the row's
  // reject banner reads it straight off the fee — no client stage math needed.
  // Closing-only slide-outs (read-only) + per-row edit state. A closing row
  // loads read-only; the Closer opens it with the pencil, edits amount +
  // disposition into a local buffer, and commits the buffer via closing-edit-fee
  // on Done (never the draft-autosave path). Approval flips fire immediately.
  const [encDiffSheetId, setEncDiffSheetId] = useState<string | null>(null);
  const [ctcSheetId, setCtcSheetId] = useState<string | null>(null);
  const [escalationSheetId, setEscalationSheetId] = useState<string | null>(null);
  const [closerEditRows, setCloserEditRows] = useState<Set<string>>(() => new Set());
  const [closerBuffers, setCloserBuffers] = useState<Record<string, Partial<DocOrderFee>>>({});
  const enterEdit = (id: string) => setCloserEditRows((p) => new Set(p).add(id));
  const bufferPatch = (feeId: string, patch: Partial<DocOrderFee>) =>
    setCloserBuffers((b) => ({ ...b, [feeId]: { ...(b[feeId] ?? {}), ...patch } }));
  const commitClosingRow = async (feeId: string) => {
    // Round-7 #2: the editor and buffer survive until the server SAYS yes —
    // discarding them first painted an edited row that was never persisted
    // (403/422/refused-flush all left false state).
    const patch = closerBuffers[feeId];
    if (patch && Object.keys(patch).length > 0) {
      const res = await closingEditFee(feeId, patch);
      if (!res.ok) {
        return; // stay in edit mode with the buffer intact; runAction toasted why
      }
    }
    setCloserEditRows((p) => {
      const n = new Set(p);
      n.delete(feeId);
      return n;
    });
    setCloserBuffers((b) => {
      const n = { ...b };
      delete n[feeId];
      return n;
    });
  };
  // Route a buffered edit (amount/disposition/payee while the pencil is open):
  // closing accumulates until Done; everyone else autosaves the draft as before.
  const routeFee = (feeId: string, patch: Partial<DocOrderFee>) => {
    if (isClosing) {
      bufferPatch(feeId, patch);
    } else {
      saveFee(feeId, patch);
    }
  };
  // Immediate one-shot commit (trust confirm, not-applicable toggle) — no Done
  // bar, so closing commits straight through closing-edit-fee.
  const commitFeeNow = (feeId: string, patch: Partial<DocOrderFee>) => {
    if (isClosing) {
      void closingEditFee(feeId, patch);
    } else {
      saveFee(feeId, patch);
    }
  };
  // Grid write convergence indicator (lo_review+). Per-fee status isn't on the
  // fee payload, so this is section-level: "queued" until every field syncs.
  const showWriteStatus = (stage === 'lo_review' || stage === 'complete') && writebacks.enabled;
  // Which rows have the split editor expanded (multiple may be open at once).
  const [splitOpen, setSplitOpen] = useState<Set<string>>(() => new Set());
  // Local split-editing buffer keyed by fee id. Split edits are immediate + local
  // (responsive), and we persist via saveFee({ splits }) only when the array is a
  // VALID intermediate or on commit (Done) — the backend rejects invalid splits
  // with 422 (which surfaces a toast), so we never send a half-allocated array.
  const [splitBuffers, setSplitBuffers] = useState<Record<string, FeeSplit[]>>({});
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<{ name: string; amount: string }>({ name: '', amount: '' });

  const readSplits = (f: DocOrderFee): FeeSplit[] => splitBuffers[f.id] ?? f.splits ?? [];

  // Un-split: the one remaining portion IS the fee — adopt its disposition so
  // nothing the processor picked is lost (parent carries no portion identity
  // under the split model, so the disposition must come from the portion).
  const unsplitPatch = (remaining: FeeSplit | undefined): Partial<DocOrderFee> => ({
    splits: null,
    ...(remaining && remaining.paidBy
      ? { paidBy: remaining.paidBy, timing: remaining.timing, reimburse: !!remaining.reimburse }
      : {})
  });

  const updateSplit = (f: DocOrderFee, splitId: string, patch: Partial<FeeSplit>) => {
    const next = readSplits(f).map((s) => (s.id === splitId ? { ...s, ...patch } : s));
    setSplitBuffers((b) => ({ ...b, [f.id]: next }));
    if (splitsValid({ amount: f.amount, splits: next })) {
saveFee(f.id, { splits: next });
}
  };
  const addSplitPortion = (f: DocOrderFee) => {
    // The new empty portion makes the allocation invalid — buffer only, no save.
    const next: FeeSplit[] = [...readSplits(f), { id: mkSplitId(), amount: 0, paidBy: null, timing: null, reimburse: false }];
    setSplitBuffers((b) => ({ ...b, [f.id]: next }));
  };
  const removeSplitPortion = (f: DocOrderFee, splitId: string) => {
    const next = readSplits(f).filter((s) => s.id !== splitId);
    setSplitBuffers((b) => {
      const n = { ...b };
      delete n[f.id];
      return n;
    });
    if (next.length <= 1) {
      // One portion = 100% of the fee — un-split back to a single-payer row.
      setSplitOpen((prev) => {
        const n = new Set(prev);
        n.delete(f.id);
        return n;
      });
      saveFee(f.id, unsplitPatch(next[0]));
    } else {
      setSplitBuffers((b) => ({ ...b, [f.id]: next }));
      if (splitsValid({ amount: f.amount, splits: next })) {
saveFee(f.id, { splits: next });
}
    }
  };
  // Opening seeds TWO portions so both split rows appear immediately below the
  // total: Split 1 = the fee's full amount + current disposition, Split 2 = an
  // empty split the processor reallocates into. Collapsing again persists a
  // valid buffer / un-splits a single portion, and discards a still-invalid
  // buffer (revert to last saved).
  const toggleSplit = (f: DocOrderFee) => {
    if (splitOpen.has(f.id)) {
      const cur = readSplits(f);
      setSplitBuffers((b) => {
        const n = { ...b };
        delete n[f.id];
        return n;
      });
      setSplitOpen((prev) => {
        const n = new Set(prev);
        n.delete(f.id);
        return n;
      });
      if (cur.length <= 1) {
saveFee(f.id, unsplitPatch(cur[0]));
} else if (splitsValid({ amount: f.amount, splits: cur })) {
saveFee(f.id, { splits: cur });
}
      return;
    }
    const cur = readSplits(f);
    const seeded: FeeSplit[] =
      cur.length >= 2
        ? cur
        : [
            cur[0] ?? { id: mkSplitId(), amount: Number(f.amount) || 0, paidBy: f.paidBy, timing: f.timing, reimburse: f.reimburse },
            { id: mkSplitId(), amount: 0, paidBy: null, timing: null, reimburse: false }
          ];
    setSplitBuffers((b) => ({ ...b, [f.id]: seeded }));
    setSplitOpen((prev) => {
      const n = new Set(prev);
      n.add(f.id);
      return n;
    });
  };
  const closeSplit = (f: DocOrderFee) => {
    const next = readSplits(f);
    setSplitBuffers((b) => {
      const n = { ...b };
      delete n[f.id];
      return n;
    });
    setSplitOpen((prev) => {
      const n = new Set(prev);
      n.delete(f.id);
      return n;
    });
    // Done is only enabled when splitsValid, so this commit is always valid.
    if (next.length <= 1) {
saveFee(f.id, unsplitPatch(next[0]));
} else {
saveFee(f.id, { splits: next });
}
  };

  // Manual amount edit from the main row. Closing buffers into the open row;
  // the processor autosaves, and an auto-reconciled fee re-opens the sheet.
  const changeAmount = (f: DocOrderFee, amount: number) => {
    if (isClosing) {
      bufferPatch(f.id, { amount, ...(f.reconciled ? { reconciled: false } : {}) });
    } else if (f.reconciled) {
      saveFee(f.id, { amount, reconciled: false });
      setReconcileId(f.id);
    } else {
      saveFee(f.id, { amount });
    }
  };

  const addRow = async () => {
    const name = draft.name.trim();
    if (!name) {
return;
}
    // addManualFee flushes pending edits, POSTs, and reconciles / toasts on its
    // own (incl. failure) — the new row appearing is the success signal.
    await addManualFee({ name, amount: Number(draft.amount) || 0 });
    setDraft({ name: '', amount: '' });
    setAdding(false);
  };

  // Only show line items that carry a charge; manual + auto-trust placeholders
  // stay visible while being processed (and everything shows while editing).
  const visibleFees = fees.filter((f) => editable || f.source === 'manual' || f.source === 'auto_trust' || Number(f.amount) > 0);
  const total = visibleFees.filter((f) => !f.notApplicable).reduce((s, f) => s + (Number(f.amount) || 0), 0);
  const encompassCount = visibleFees.filter((f) => f.source === 'encompass').length;

  return (
    <div className="card card-pad snapshot-compact">
      <div className="ws-section-head">
        <h3 className="ws-section-title">
          <span className="ws-step-no">{stepNo}</span>Third-Party Fees
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {showWriteStatus &&
            (writebacks.pendingFields > 0 ? (
              <span className="fee-write-status pending">
                <Icon name="clock" size={12} />
                {writebacks.pendingFields} queued to Encompass
                <button type="button" className="fee-write-retry" disabled={busy} onClick={() => void retryWrites()}>
                  Retry
                </button>
              </span>
            ) : (
              <span className="fee-write-status synced">
                <Icon name="checkCircle" size={12} />
                Synced to Encompass
              </span>
            ))}
          <StatusPill kind="info" icon="link">
            {encompassCount} from Encompass
          </StatusPill>
        </div>
      </div>

      {editable && role !== 'closing' && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            margin: '-4px 0 10px',
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--bad-fg)',
            letterSpacing: '0.01em'
          }}
        >
          <Icon name="alertTriangle" size={13} style={{ flexShrink: 0 }} />
          <span>Enter fees exactly as invoiced — no estimates. Amounts disclose as entered.</span>
        </div>
      )}

      {editable && role !== 'closing' && (
        <div className="rail-note" style={{ display: 'flex', alignItems: 'flex-start', gap: 7, marginBottom: 10 }}>
          <Icon name="link" size={12} style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 2 }} />
          <span>Prefilled from Encompass §800.</span>
        </div>
      )}

      <div className={`fee-inline-list ${isClosing ? 'with-encpaid' : ''}`.trim()}>
        <div className="fee-inline-head">
          <span className="col-name">Fee</span>
          <span className="col-amt">Amount</span>
          <span className="col-disp">Disposition</span>
          {isClosing && <span className="col-encpaid">Paid By</span>}
          <span className="col-recon"></span>
          <span className="col-split"></span>
          <span className="col-invoice">Invoice</span>
        </div>

        {visibleFees.map((f) => {
          const feeAmtNum = Number(f.amount) || 0;
          const isCharged = feeAmtNum > 0;
          const isTrustPending = f.source === 'auto_trust' && !f.confirmed && !f.notApplicable;
          const folderHasDocs = invoiceFolderStatus[f.feeKey] ?? false;
          // Evidence split (Batch 3.1 F2 amendment): CURRENT EVIDENCE (filed
          // invoices / eFolder mirror docs — the dot/gate truth, via
          // feeInvoiceStatus) is a separate concept from UPLOAD ACTIVITY
          // (queued/failed/filed local entries — which keep the sheet and its
          // Check-status recovery REACHABLE but never claim "in eFolder").
          const localEntries = f.invoices ?? [];
          const hasQueuedUpload = localEntries.some((i) => i.uploadStatus === 'queued');
          const heldUpload = localEntries.some((i) => i.uploadStatus === 'queued' && i.uploadError);
          const hasFailedUpload = localEntries.some((i) => i.uploadStatus === 'failed');
          const invoiceReject = f.acks?.invoiceReject ?? null;
          const filedInvoiceCount = activeFiledInvoices(f).length;
          const eFolderDocCount = invoiceFolderDocs[f.feeKey]?.docs.length ?? 0;
          const presentInvoiceCount = Math.max(eFolderDocCount, filedInvoiceCount, folderHasDocs ? 1 : 0);
          const hasCurrentEvidence = folderHasDocs || filedInvoiceCount > 0;
          const hasDiscrepancy = invoiceHasDiscrepancy(f);
          // An ack only stands while it covers the CURRENT delta — a later
          // amount/invoice change makes it stale automatically. ONE runtime-
          // validated rule (isCurrentFeeAck) shared with the server gate (F7).
          const invDelta = invoiceTotal(f) - feeAmtNum;
          const escalationAck = f.acks?.escalation;
          const shortfallAck = f.acks?.shortfall;
          // R5: escalation currency is KIND-AWARE and independent of
          // invoiceHasDiscrepancy (review-4 #2 — a zero-invoice escalation
          // must still read current so the pill can say "Escalated").
          const currentEscalation = isCurrentFeeEscalation(f, escalationAck);
          const reconciledShortfall = isCurrentFeeAck(shortfallAck, invDelta);
          const mismatch = isCharged && !f.notApplicable && hasDiscrepancy && !currentEscalation && !reconciledShortfall;
          const isRefreshFee = f.refreshCredit !== undefined;
          const needsRefresh = isRefreshFee && f.refreshCredit === null;
          const statusDot = isTrustPending ? 'pending' : feeInvoiceStatus(f, folderHasDocs);
          // R5 #3 — TWO rules, never conflated: the DOT is EVIDENCE truth
          // (feeInvoiceStatus — an escalation never makes it green) while the
          // WORKFLOW requirement is what a current server-typed
          // missing_invoice escalation closes (pill/rollup/defer).
          const workflowOpen = !isTrustPending && feeInvoiceWorkflowOpen(f, folderHasDocs);
          const reconciliationOpen = !isTrustPending && feeReconciliationWorkflowOpen(f, folderHasDocs);
          // Tooltip states the SPECIFIC evidence/activity — never "in eFolder"
          // off a queued/failed entry (raw invoices.length is banned here).
          const dotTitle = f.notApplicable
            ? 'Not applicable'
            : isTrustPending
              ? 'Trust Review Fee — not yet confirmed'
              : !isCharged
                ? 'No charge — invoice not required'
                : hasCurrentEvidence
                  ? `${presentInvoiceCount} invoice${presentInvoiceCount === 1 ? '' : 's'} on file in the eFolder`
                  : heldUpload
                    ? 'Invoice upload held — the loan is open in Encompass'
                    : hasQueuedUpload
                      ? 'Invoice uploading to Encompass — not filed yet'
                      : hasFailedUpload
                        ? 'Invoice upload failed — re-upload'
                        : 'Invoice missing from eFolder';
          // A fee is "split" once it carries 2+ payer portions. Jennifer's model
          // (bugs 10/11): the parent row shows ONLY the total — no disposition,
          // no portion identity — and ALL portions render below as Split 1..N,
          // summing to the total. (Supersedes the prototype's parent-is-portion-1
          // aliasing, per the designer's testing feedback.)
          const splits = readSplits(f);
          const isSplit = isCharged && !f.notApplicable && splits.length >= 2;
          // splitOpen is component state and the keyed sec-fees instance SURVIVES
          // the submit transition (R4 review): an editor left open in draft must
          // COLLAPSE to the settled read-only rows the moment the viewer/stage
          // can no longer write the 'fees' save-draft section — its controls
          // call saveFee, which hard-403s at submitted and would stall the drain.
          const splitEditing = isSplit && splitOpen.has(f.id) && editable && !isClosing;

          // ---- closing-only: read-only-until-pencil, buffered edits, flags ----
          const closerRO = isClosing && !closerEditRows.has(f.id);
          const rowEditable = editable && !closerRO;
          const buffered = isClosing ? closerBuffers[f.id] : undefined;
          // Display honors the in-flight closing buffer (amount + disposition).
          const df: DocOrderFee = buffered ? { ...f, ...buffered } : f;
          const feeAmtNow = Number(df.amount) || 0;
          const invTotalNow = invoiceTotal(f);
          const encMismatch = f.encompassAmount !== null && Math.abs(Number(f.encompassAmount) - feeAmtNow) >= DO_TOLERANCE.discrepancy;
          const invMismatch = invTotalNow > DO_TOLERANCE.costToCure && Math.abs(invTotalNow - feeAmtNow) >= DO_TOLERANCE.discrepancy;
          const encDiff = isClosing && isCharged && !f.notApplicable && (encMismatch || invMismatch);
          const ctc = isClosing && isCharged && !f.notApplicable ? costToCureCheck(f) : null;
          const hasCtc = !!ctc?.overDisclosed;

          return (
            <div key={f.id} className={`fee-inline-wrap ${f.notApplicable ? 'na' : ''} ${isTrustPending ? 'pending' : ''}`}>
              {isTrustPending && (
                <div className="fee-banner-thin warn">
                  <Icon name="alertCircle" size={12} />
                  <span>
                    <b>Closing in Trust</b> — confirm an amount + invoice or mark not applicable.
                  </span>
                  {rowEditable && (
                    <div className="fee-banner-actions">
                      <button className="ftb-confirm" disabled={!isCharged} onClick={() => commitFeeNow(f.id, { confirmed: true })}>
                        Confirm fee
                      </button>
                      <button
                        className="ftb-na"
                        onClick={() => commitFeeNow(f.id, { notApplicable: true, amount: 0, timing: null, reimburse: false })}
                      >
                        Not applicable
                      </button>
                    </div>
                  )}
                </div>
              )}
              {f.notApplicable && (
                <div className="fee-banner-thin na">
                  <Icon name="x" size={12} />
                  <span>
                    <b>{f.name}</b> — Not applicable. Excluded from the fee itemization.
                  </span>
                  {rowEditable && (
                    <button className="ftb-reset" onClick={() => commitFeeNow(f.id, { notApplicable: false })}>
                      Undo
                    </button>
                  )}
                </div>
              )}

              <div
                className={`fee-inline-row ${isSplit ? 'has-split-alloc' : ''} ${feeNeedsDisposition(df) ? 'unset' : ''} ${
                  (mismatch && rowEditable) || ((encDiff || hasCtc) && !f.approvedBy) ? 'review' : ''
                }`.trim()}
              >
                <div className="col-name">
                  <div className="fee-name-block">
                    <span className="fee-name-line">
                      <span className="fee-name-text">{f.name}</span>
                      {isSplit && <span className="fee-split-badge">Split</span>}
                      {/* #20: only user-added ('manual') or auto-injected
                          ('auto_trust') fees can be deleted — an Encompass §800
                          line uses Not Applicable. Processor draft only. */}
                      {rowEditable && !isClosing && (f.source === 'manual' || f.source === 'auto_trust') && (
                        <IconBtn
                          name="trash"
                          size={12}
                          title="Remove this fee"
                          className="plain fee-delete-btn"
                          onClick={async () => {
                            const res = await deleteFee(f.id);
                            if (res.ok) {
                              addToast(`Removed ${f.name}`, 'trash');
                            }
                          }}
                        />
                      )}
                    </span>
                    {!(isSplit && !splitEditing) &&
                      (rowEditable ? (
                        <input
                          className="fee-payee-sub-inp"
                          placeholder="add payee…"
                          value={df.payee ?? ''}
                          onChange={(e) => routeFee(f.id, { payee: e.target.value })}
                        />
                      ) : df.payee ? (
                        <span className="fee-payee-sub">{df.payee}</span>
                      ) : null)}
                  </div>
                </div>

                <div className="col-amt">
                  {rowEditable && !(isSplit && !splitEditing) ? (
                    <div className="fee-amount-wrap">
                      <span className="fee-amount-prefix">$</span>
                      <input
                        className="fee-amount-inp"
                        type="number"
                        min="0"
                        step="0.01"
                        value={df.amount}
                        onChange={(e) => changeAmount(f, Number(e.target.value))}
                      />
                    </div>
                  ) : (
                    <span className="fee-amount-ro">{fmtMoney(df.amount, true)}</span>
                  )}
                </div>

                <div className="col-disp">
                  {/* Split fees carry NO disposition on the parent row — the
                      portions below own it (Jennifer bug 11). */}
                  {!isSplit && (
                    <DispositionCell
                      disp={df}
                      editable={rowEditable}
                      charged={isCharged}
                      onChange={(patch) => routeFee(f.id, patch)}
                    />
                  )}
                </div>

                {isClosing && (
                  <div className="col-encpaid">
                    {isCharged && !f.notApplicable ? (
                      <span className="encpaid-pill" title="Amount Paid by — synced from Encompass §800">
                        {f.encPaidBy || '—'}
                      </span>
                    ) : (
                      <span className="encpaid-na">—</span>
                    )}
                  </div>
                )}

                <div className="col-recon">
                  {rowEditable && !isClosing && isCharged && !f.notApplicable && !isTrustPending && (
                    // R5 #3 ordered precedence (reviews 3/4): the WORKFLOW rule
                    // gates rule 1 (a current missing_invoice escalation
                    // resolves it — Jennifer's ask), and "Escalated" keys on
                    // the kind-aware currentEscalation (NO discrepancy
                    // prerequisite — a zero-invoice escalation reads
                    // Escalated, not Reconciled):
                    //  1. workflow open → attn "Needs invoice"
                    //  2. refresh unanswered → attn "Reconcile"
                    //  3. current escalation → neutral "Escalated"
                    //  4. reconciled → settled "Reconciled"
                    //  5. otherwise → attn "Reconcile"
                    <button
                      className={`recon-btn ${
                        reconciliationOpen || needsRefresh ? 'attn' : currentEscalation ? 'neutral' : f.reconciled ? 'settled' : 'attn'
                      }`}
                      title="Reconcile"
                      onClick={() => setReconcileId(f.id)}
                    >
                      {workflowOpen
                        ? 'Needs invoice'
                        : needsRefresh
                          ? 'Reconcile'
                          : reconciliationOpen
                            ? 'Reconcile'
                            : currentEscalation
                              ? 'Escalated'
                              : f.reconciled
                                ? 'Reconciled'
                                : 'Reconcile'}
                    </button>
                  )}
                  {/* R5 review-3 #5 — the CLOSING-facing destination: the note
                      is addressed to Closing, but the Reconcile sheet is
                      processor-only. Read-only chip + detail sheet. */}
                  {isClosing && isCharged && !f.notApplicable && currentEscalation && escalationAck && (
                    <button
                      type="button"
                      className="recon-btn neutral"
                      title="Escalated by Processing — view the note"
                      onClick={() => setEscalationSheetId(f.id)}
                    >
                      Escalated
                    </button>
                  )}
                </div>

                <div className="col-split">
                  {rowEditable && !isClosing && isCharged && !f.notApplicable && (
                    <button
                      type="button"
                      className={`fee-add-split-entry ${splitOpen.has(f.id) || isSplit ? 'on' : ''}`}
                      onClick={() => toggleSplit(f)}
                    >
                      <Icon name="plus" size={12} />
                      Split
                    </button>
                  )}
                </div>

                <div className="col-invoice">
                  {rowEditable && isCharged && !f.notApplicable && (
                    <button
                      type="button"
                      className={`fee-upload-btn ${uploadingFeeId === f.id ? 'is-open' : ''}`.trim()}
                      title={
                        // Truthful state first (Batch 3.1): active states (held/
                        // queued) name themselves; then FILED EVIDENCE — failed
                        // entries are append-only, so after a successful retry
                        // the stale failure must not outrank the current invoice
                        // (rollout finding: failed-then-filed said "failed").
                        // Failed speaks only when nothing current exists.
                        heldUpload
                          ? 'Upload held — the loan is open in Encompass; it files when the lock clears. You can upload another.'
                          : hasQueuedUpload
                            ? 'An invoice upload is in progress — not filed yet. You can upload another.'
                            : hasCurrentEvidence
                              ? `${presentInvoiceCount} invoice${presentInvoiceCount === 1 ? '' : 's'} in eFolder — click to upload another`
                              : hasFailedUpload
                                ? 'Last invoice upload failed — upload again'
                                : 'Upload invoice (AI compares the amount to the fee)'
                      }
                      onClick={() => setUploadingFeeId(uploadingFeeId === f.id ? null : f.id)}
                    >
                      <Icon name="upload" size={12} />
                    </button>
                  )}
                  {encDiff && (
                    <button type="button" className="fee-ctc-flag enc" title="Doc order varies from Encompass" onClick={() => setEncDiffSheetId(f.id)}>
                      <Icon name="unlink" size={14} />
                    </button>
                  )}
                  {hasCtc && (
                    <button type="button" className="fee-ctc-flag" title="Potential Cost to Cure — view details" onClick={() => setCtcSheetId(f.id)}>
                      <Icon name="alertTriangle" size={14} />
                    </button>
                  )}
                  {f.reconciled && statusDot === 'ok' ? (
                    // Check ONLY when the dot truth is 'ok' (Batch 3.1 F2) — a
                    // reconciled fee whose invoice requirement is open (or 'na'/
                    // 'pending') keeps its truthful dot. Tooltip never claims an
                    // invoice match for a shortfall-based reconcile.
                    <span
                      className="fee-dot-recon"
                      title={
                        reconciledShortfall
                          ? 'Reconciled with an escalated shortfall — no full invoice'
                          : 'Auto-reconciled — invoice matches the last disclosed fee'
                      }
                    >
                      <Icon name="checkCircle" size={14} />
                    </span>
                  ) : (
                    <span className={`fee-dot fee-dot-${statusDot}`} title={dotTitle} />
                  )}
                  {(() => {
                    // Invoice eye popover — mirror-backed metadata (bug 7).
                    const peek = invoiceFolderDocs[f.feeKey];
                    return peek ? <FeePeek id={f.id} data={peek} addToast={addToast} /> : null;
                  })()}
                  {isClosing && isCharged && !f.notApplicable && !isSplit && closerRO && (
                    <button type="button" className="fee-edit-btn" title="Edit this fee" disabled={busy || !editable} onClick={() => enterEdit(f.id)}>
                      <Icon name="edit" size={13} />
                      Edit
                    </button>
                  )}
                  {isClosing && isCharged && !f.notApplicable && (
                    <label
                      className={`fee-approve ${f.approvedBy ? 'on' : ''}`}
                      title={f.approvedBy ? `Approved as entered${f.approvedAt ? ` · ${fmtStamp(f.approvedAt)}` : ''}` : 'Approve this fee as entered — clears it even with discrepancies or cost-to-cure alerts'}
                    >
                      <input type="checkbox" checked={!!f.approvedBy} disabled={!editable || busy} onChange={(e) => void approveFee(f.id, e.target.checked)} />
                      <span>Approve</span>
                    </label>
                  )}
                </div>
              </div>
              {isClosing && isCharged && !f.notApplicable && !isSplit && !closerRO && (
                <div className="fee-edit-done-bar">
                  <span>
                    <Icon name="edit" size={11} />
                    Editing this fee — make changes above, then
                  </span>
                  <button type="button" className="fee-edit-btn done" disabled={busy || !editable} onClick={() => void commitClosingRow(f.id)}>
                    <Icon name="check" size={13} />
                    Done
                  </button>
                </div>
              )}

              {invoiceReject && (
                <div className="fee-row-reject">
                  <div className="im-reject">
                    <Icon name="alertTriangle" size={11} />
                    <span>
                      Validation failed on “{invoiceReject.file}”{invoiceReject.detected ? ` — reads as a ${invoiceReject.detected}` : ''}.
                      Requirement reopened — re-upload the correct invoice.
                      {invoiceReject.afterSubmit && <b> Flagged to Closing.</b>}
                    </span>
                  </div>
                </div>
              )}

              {rowEditable && uploadingFeeId === f.id && (
                <div className="fee-row-upload">
                  <UploadPanel mode="invoice" fee={f} fees={fees} onClose={() => setUploadingFeeId(null)} />
                </div>
              )}

              {isSplit &&
                (() => {
                  const feeTotal = feeAmtNum;
                  const allocated = splits.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);
                  const diff = feeTotal - allocated;
                  const allFilled = splits.length >= 2 && splits.every((s) => Number(s.amount) > 0);
                  const allocOk = Math.abs(diff) < DO_TOLERANCE.discrepancy && allFilled;
                  // Done gates on the full splitsValid (amounts + allocation + a
                  // payer on every portion) to match the backend and avoid a 422.
                  const ok = splitsValid({ amount: f.amount, splits });
                  return (
                    <>
                      {splits.map((s, i) => {
                        // EVERY portion renders below the total — the parent row
                        // is never a portion (Jennifer bug 10).
                        const pct = feeTotal ? Math.round(((Number(s.amount) || 0) / feeTotal) * 1000) / 10 : 0;
                        return (
                          <div key={s.id} className={`fee-split-alloc-row ${splitEditing ? '' : 'settled'}`}>
                            <div className="col-name fee-split-tag">
                              <span className="fee-split-ix">Split {i + 1}</span>
                              <span className="fee-split-pct">{pct}%</span>
                            </div>
                            <div className="col-amt">
                              {splitEditing ? (
                                <div className="fee-amount-wrap">
                                  <span className="fee-amount-prefix">$</span>
                                  <input
                                    className="fee-amount-inp"
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={s.amount}
                                    onChange={(e) => updateSplit(f, s.id, { amount: Number(e.target.value) })}
                                  />
                                </div>
                              ) : (
                                <span className="fee-amount-ro">{fmtMoney(s.amount, true)}</span>
                              )}
                            </div>
                            <div className="col-disp">
                              <DispositionCell disp={s} editable={splitEditing} onChange={(patch) => updateSplit(f, s.id, patch)} />
                            </div>
                            {isClosing && (
                              <div className="col-encpaid">
                                <span className="encpaid-pill" title="Amount Paid by — synced from Encompass §800">
                                  {s.encPaidBy || f.encPaidBy || '—'}
                                </span>
                              </div>
                            )}
                            <div className="col-recon"></div>
                            <div className="col-split"></div>
                            <div className="col-invoice">
                              {splitEditing && (
                                <IconBtn name="trash" size={12} title="Remove split" onClick={() => removeSplitPortion(f, s.id)} className="plain" />
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {splitEditing ? (
                        <div className="fee-split-foot-row">
                          <button type="button" className="fee-add-split" onClick={() => addSplitPortion(f)}>
                            <Icon name="plus" size={12} />
                            Add split
                          </button>
                          <div className="fee-split-foot-right">
                            <span className="fee-split-total">
                              <span className="fee-split-total-lbl">Total fee</span> {fmtMoney(feeTotal, true)}
                            </span>
                            <span className={`fee-split-alloc ${ok ? 'ok' : 'warn'}`}>
                              <Icon name={ok ? 'check' : 'alertCircle'} size={12} />
                              {!allFilled ? (
                                'Each split needs an amount'
                              ) : !allocOk ? (
                                <>
                                  Allocated {fmtMoney(allocated, true)} / {fmtMoney(feeTotal, true)}
                                  <span className="fee-split-rem">
                                    ({fmtMoney(Math.abs(diff), true)} {diff > 0 ? 'left' : 'over'})
                                  </span>
                                </>
                              ) : !ok ? (
                                'Each split needs a payer'
                              ) : (
                                <>
                                  Allocated {fmtMoney(allocated, true)} / {fmtMoney(feeTotal, true)}
                                </>
                              )}
                            </span>
                            <button
                              type="button"
                              className="fee-split-close recon-btn neutral"
                              data-preserve-disabled
                              disabled={!ok}
                              title={
                                ok
                                  ? undefined
                                  : !allFilled
                                    ? 'Every split portion needs an amount greater than $0 before saving'
                                    : !allocOk
                                      ? `Allocations must equal the total fee (${fmtMoney(Math.abs(diff), true)} ${diff > 0 ? 'left to allocate' : 'over'}) before saving`
                                      : 'Every split portion needs a Paid Upfront selection before saving'
                              }
                              onClick={() => {
                                if (ok) {
                                  closeSplit(f);
                                }
                              }}
                            >
                              Done
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="fee-split-foot-row settled">
                          <div className="fee-split-foot-payee">
                            {/* Draft-only (R4 review): saveFee posts the 'fees'
                                save-draft section, which hard-403s at submitted
                                — a closer reaching this input would stall the
                                drain and block Send. Splits are read-only for
                                Closing (the pencil edit excludes them too). */}
                            {editable && !isClosing ? (
                              <input
                                className="fee-payee-sub-inp"
                                placeholder="add payee…"
                                value={f.payee ?? ''}
                                onChange={(e) => saveFee(f.id, { payee: e.target.value })}
                              />
                            ) : f.payee ? (
                              <span className="fee-payee-sub">{f.payee}</span>
                            ) : null}
                            {!ok && (
                              <span className="fee-split-status warn">
                                <Icon name="alertCircle" size={12} />
                                {`${fmtMoney(Math.abs(diff), true)} ${diff > 0 ? 'unallocated' : 'over'}`}
                              </span>
                            )}
                          </div>
                          <div className="fee-split-foot-right">
                            <span className="fee-split-total">
                              <span className="fee-split-total-lbl">Total fee</span> {fmtMoney(feeTotal, true)}
                            </span>
                            {editable && !isClosing && (
                              <button type="button" className="fee-split-edit" onClick={() => toggleSplit(f)}>
                                Edit split
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
            </div>
          );
        })}

        {visibleFees.length === 0 && (
          <div style={{ padding: '22px 8px', textAlign: 'center', color: 'var(--fg-subtle)', fontSize: 'var(--t-sm)' }}>
            No third-party fees on this loan.
          </div>
        )}
        <div className="fee-inline-total">
          <span>Total third-party fees</span>
          <b>{fmtMoney(total, true)}</b>
        </div>
      </div>

      {reconcileId &&
        (() => {
          const rf = fees.find((x) => x.id === reconcileId);
          return rf ? <ReconcileSheet fee={rf} onClose={() => setReconcileId(null)} /> : null;
        })()}

      {encDiffSheetId &&
        (() => {
          const ef = fees.find((x) => x.id === encDiffSheetId);
          return ef ? (
            <EncompassDiffSheet
              fee={ef}
              onUseEncompass={() => {
                void closingEditFee(ef.id, { amount: Number(ef.encompassAmount) || 0 });
                setEncDiffSheetId(null);
              }}
              onKeepSubmitted={() => {
                void closingEditFee(ef.id, { acks: { ...ef.acks, encompassResolve: 'keep' } });
                setEncDiffSheetId(null);
              }}
              onClose={() => setEncDiffSheetId(null)}
            />
          ) : null;
        })()}

      {ctcSheetId &&
        (() => {
          const cf = fees.find((x) => x.id === ctcSheetId);
          return cf ? <CostToCureSheet fee={cf} onClose={() => setCtcSheetId(null)} /> : null;
        })()}
      {escalationSheetId &&
        (() => {
          const ef = fees.find((x) => x.id === escalationSheetId);
          return ef ? <EscalationSheet fee={ef} onClose={() => setEscalationSheetId(null)} /> : null;
        })()}

      {editable &&
        !isClosing &&
        (adding ? (
          <div className="fee-add">
            <input
              className="inp"
              style={{ flex: 2 }}
              placeholder="Fee name (e.g. Subordination Fee)"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              autoFocus
            />
            <input
              className="inp"
              style={{ width: 130 }}
              type="number"
              placeholder="Amount"
              value={draft.amount}
              onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
            />
            <Btn variant="primary" size="sm" icon="check" onClick={() => void addRow()}>
              Add
            </Btn>
            <Btn variant="ghost" size="sm" onClick={() => setAdding(false)}>
              Cancel
            </Btn>
          </div>
        ) : (
          <div className="fee-add">
            <Btn variant="ghost" size="sm" icon="plus" onClick={() => setAdding(true)}>
              Add manual fee
            </Btn>
            <span className="rail-note" style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 10 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span className="fee-dot fee-dot-ok" /> invoice in eFolder
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span className="fee-dot fee-dot-missing" /> missing
              </span>
            </span>
          </div>
        ))}
    </div>
  );
}
